import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import axios from "@utils/axios";
import { AdhocTextInterface } from "../types/adhoc";

export interface AdhocTextParams {
  evseID: string;
}

const fetchAdhocText = async (params: AdhocTextParams, signal?: AbortSignal): Promise<AdhocTextInterface> => {
  const response = await axios.get(`/api/evse/${params.evseID}/get-design/`, { signal });
  const data = response.data;

  return {
    en: data.stripeEn,
    de: data.stripeDe
  } as AdhocTextInterface;
};

type QueryKeyType = readonly ["adhoc", "text", AdhocTextParams | "disabled"];

export const adhocTextKeys = {
  all: ["adhoc"] as const,
  texts: () => [...adhocTextKeys.all, "text"] as const,
  text: (params: AdhocTextParams | "disabled") => [...adhocTextKeys.texts(), params] as const
};

export const useAdhocText = (
  params: AdhocTextParams | null,
  options?: Omit<UseQueryOptions<AdhocTextInterface, Error, AdhocTextInterface, QueryKeyType>, "queryKey" | "queryFn">
) => {
  const isEnabled = params !== null && !!params.evseID;

  return useQuery({
    queryKey: params ? adhocTextKeys.text(params) : adhocTextKeys.text("disabled"),
    queryFn: ({ signal }) => {
      if (!params) {
        throw new Error("Adhoc text params are required");
      }
      return fetchAdhocText(params, signal);
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: isEnabled,
    ...options
  });
};
